import Dialog from 'components/Dialog';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {usePostApi} from 'common/hooks/usePostApi';
import api from 'api';
import history from 'redux/history';
import {packId} from 'common/diplomatIdUtil';
import actions from 'features/layout/redux/actions';
import FormikTextField from 'components/inputs/FormikTextField';
import {useTranslation} from 'react-i18next';
import {IDiplomatObject} from 'types/DiplomatObject';
import {FormFooterButtons} from 'components/buttons/FormFooterButtons';
import {layoutSelectors} from 'features/layout/redux';
import {useModal} from 'common/hooks/useModal';
import {ConfirmationDialog} from 'components/dialogs/ConfirmationDialog';
import {ISynchronization} from 'types/diplomat/transaction/Synchronization';
import {DialogTypes} from 'common/names';
import {synchronizationsSelectors} from 'redux/synchronizations';
import Formik, {FormOnSubmitHelpers} from 'components/hook-form/Formik';
import {DiplomatObjectType} from 'types/DiplomatObjectType';

type CreateSynchronizationProps = {
   open?: boolean;
   data?: ISynchronization;
   onClose: () => void;
};

type INameForm = {name: string};

const CreateSynchronizationDialog = ({open = true, data, onClose}: CreateSynchronizationProps) => {
   const {t} = useTranslation();
   const dispatch = useDispatch();

   const [formValues, setFormValues] = React.useState<INameForm>();

   const folderId = useSelector(layoutSelectors.synchronizationFolderId);

   const onSuccess = (data: ISynchronization) => {
      dispatch(actions.loadTreeNav());
      onClose();
      history.push(`/${packId(data.objectType, data.id)}`);
   };
   const {request} = usePostApi<ISynchronization>(api.synchronizations, {
      onSuccess,
      onFailure: onClose,
   });
   const handleSubmit = (values?: INameForm) => {
      const payload = {
         synchronizationName: values?.name || formValues?.name,
         overwrite: !values?.name,
         folderId: data?.id || folderId,
      };
      // if (!payload.folderId) {
      //    delete payload.folderId;
      // }
      request(JSON.stringify(payload));
   };

   const {open: alertOpened, handleOpen: openAlert, handleClose: closeAlert} = useModal();
   const synchronizations = useSelector(layoutSelectors.transactionsSyncsSelector);

   const helpersRef = React.useRef<FormOnSubmitHelpers<INameForm> | null>(null);

   const validateAndSave = (values: INameForm, helpers: FormOnSubmitHelpers<INameForm>) => {
      setFormValues(values);
      const {name} = values;
      const transaction = synchronizations.find((obj: IDiplomatObject) => obj.name === name);
      if (transaction) {
         if (transaction.objectType !== DiplomatObjectType.enum.SYNCHRONIZATION) {
            helpers.setErrors({
               name: t('Diplomat already contains a {{type}} transaction named {{name}}', {
                  type: DiplomatObjectType[transaction.objectType],
                  name,
               }),
            });
            helpers.setSubmitting(false);
         } else {
            // Object.assign(helpersRef.current, helpers);
            helpersRef.current = helpers;
            openAlert();
         }
         return;
      }

      return handleSubmit(values);
   };

   const onCloseAlert = React.useCallback(() => {
      helpersRef.current?.setSubmitting(false);
      closeAlert();
   }, [helpersRef, closeAlert]);

   return (
      <>
         <Dialog
            maxWidth="sm"
            header={DialogTypes.CreateSynchronization.dialogName}
            open={open}
            close={onClose}
         >
            <Formik initialValues={{name: ''}} onSubmit={validateAndSave}>
               <>
                  <FormikTextField
                     autoFocus
                     required
                     label={t('New Synchronization Name:')}
                     name={'name'}
                  />
                  <FormFooterButtons cancel={onClose} />
               </>
            </Formik>
         </Dialog>
         <ConfirmationDialog
            open={alertOpened}
            content={t(
               'Diplomat already contains a synchronizations with this name. \n Do you want to override it?'
            )}
            onOk={handleSubmit}
            onCancel={onCloseAlert}
         />
      </>
   );
};

export default CreateSynchronizationDialog;
